import {
  ATTRIBUTE_OPTION_LIMITS,
  attributeOptionMatch,
  normaliseAttributeOptions,
  type GeneratedDefinition,
  type GeneratedNodeAttribute,
} from "@keydials/api-client";
import { fieldsetMembers, offeredSettings, type DetailsSettingId, type ItemScopeTreeLabels, type SegmentNoun } from "@keydials/dial-workspace";
import { builderCopy as copy, fieldName, fill, nounValues } from "./copy";

// ---------------------------------------------------------------------------
// A field's settings on Details (KEY-560, Phase 3): which settings a field shows, and what each says. The edits are the shared field
// edits; this decides what the settings area shows, so it can be tested without a browser.
// ---------------------------------------------------------------------------

type DetailsWords = typeof copy.details;
/** The keys of the Details copy that hold a sentence rather than a group of them. */
type DetailsWord = { [K in keyof DetailsWords]: DetailsWords[K] extends string ? K : never }[keyof DetailsWords];

/** Where each setting's name lives in the copy, whose keys were approved before the settings had ids. */
const SETTING_WORDS: Record<DetailsSettingId, DetailsWord> = {
  options: "options",
  peopleCanAddTheirOwn: "peopleAddOwn",
  group: "group",
  startsFolded: "startsFolded",
  suggestedAnswer: "suggestedAnswer",
  mustAnswerBeforeOpening: "requiredAtFirstRun",
  mustFillBeforeWork: "requiredForNode",
  onlySomeItems: "onlySomeItems",
  allowMoreThanOne: "allowMultiple",
  whatToTellPeople: "whatToTellPeople",
};

export interface SettingView {
  id: DetailsSettingId;
  /** The setting's name, with the dial's own word for an action point filled in. */
  label: string;
}

/**
 * The settings a field shows, in the order they're offered, each with its name. What to tell people is left out while the field's
 * guidance is formatted text a template brought, which a plain box would show as markup and then save over; `guidanceIsMarkup`
 * says whether it is, and the settings area judges it once, so typing a bracket can't hide the box being typed in.
 */
export function fieldSettings(field: GeneratedNodeAttribute, noun: SegmentNoun, guidanceIsMarkup = guidanceHasMarkup(field.guidance)): SettingView[] {
  const words = nounValues(noun);
  return offeredSettings(field)
    .filter((id) => id !== "whatToTellPeople" || !guidanceIsMarkup)
    .map((id) => ({ id, label: fill(copy.details[SETTING_WORDS[id]], words) }));
}

/** The name of a field's "More options" button: "More options for Serial number". */
export function moreOptionsLabel(label: string): string {
  return fill(copy.details.moreOptionsFor, { name: fieldName(label) });
}

/**
 * Whether guidance holds HTML markup, as guidance written in the admin editor's formatted text box does. The builder writes plain
 * text, which is what the screen before a dial opens shows.
 */
export function guidanceHasMarkup(guidance: string | null | undefined): boolean {
  return /<\/?[a-z][a-z0-9]*(\s[^<>]*)?\/?>/i.test(guidance ?? "");
}

// ---- options ----------------------------------------------------------------------------------------------------------------

/** The line an empty list of options shows: what to add, or, on a list people can add their own to, what happens until then. */
export function optionsEmptyLine(field: Pick<GeneratedNodeAttribute, "options" | "allowUserValues">): string | null {
  if ((field.options ?? []).length > 0) return null;
  return field.allowUserValues ? copy.details.optionsEmptyOpen : copy.details.optionsEmpty;
}

/**
 * For each option box, whether it repeats one above it, compared as the server compares options when it drops the repeats: trimmed,
 * clamped and ignoring case. An empty box repeats nothing.
 */
export function duplicateOptions(options: readonly string[]): boolean[] {
  const seen = new Set<string>();
  return options.map((option) => {
    const folded = option.trim().slice(0, ATTRIBUTE_OPTION_LIMITS.maxLength).toLowerCase();
    if (!folded) return false;
    if (seen.has(folded)) return true;
    seen.add(folded);
    return false;
  });
}

/** Why another option can't be added, or null when it can: a list holds up to 200. */
export function addOptionRefusal(options: readonly string[]): string | null {
  const limit = ATTRIBUTE_OPTION_LIMITS.maxOptions;
  return options.length >= limit ? fill(copy.details.optionsLimit, { n: limit }) : null;
}

/** An option box's name, counting from one: "Option 3". */
export function optionLabel(index: number): string {
  return fill(copy.details.optionLabel, { n: index + 1 });
}

/** The name of an option's Remove button: "Remove Option 3". */
export function removeOptionLabel(index: number): string {
  return `${copy.build.remove} ${optionLabel(index)}`;
}

// ---- group ------------------------------------------------------------------------------------------------------------------

export interface RenameGroupView {
  /** The group's heading, as it will be stored. */
  title: string;
  /** "Rename group (3)". */
  button: string;
  /** "New name for Evidence". */
  formLabel: string;
}

/** The way to rename a field's whole group, offered only while the group has more than one field at its level. */
export function renameGroupView(model: GeneratedDefinition, field: GeneratedNodeAttribute): RenameGroupView | null {
  const title = field.fieldsetTitle?.trim();
  if (!title) return null;
  const count = fieldsetMembers(model.nodeAttributes ?? [], field.target, title).length;
  if (count < 2) return null;
  return { title, button: fill(copy.details.renameGroup, { count }), formLabel: fill(copy.details.renameGroupLabel, { name: title }) };
}

// ---- suggested answer -------------------------------------------------------------------------------------------------------

/** The box a suggested answer is written in, which follows the kind of answer the field takes. */
export type SuggestedAnswerInput =
  | { kind: "text"; value: string }
  | { kind: "longText"; value: string }
  | { kind: "date"; value: string }
  /** A closed choice: one of its options, or none. `value` is the option's own spelling, or empty. */
  | { kind: "pick"; value: string; options: string[] }
  /** A closed choice with no options to pick from yet. */
  | { kind: "needsOptions" }
  /** A choice people can add their own to: anything, with its options offered. */
  | { kind: "openChoice"; value: string; options: string[] };

/** How a field's suggested answer is written, or null for a kind of answer that can't have one. */
export function suggestedAnswerInput(field: GeneratedNodeAttribute): SuggestedAnswerInput | null {
  const value = field.defaultValue ?? "";
  switch (field.type) {
    case "Text":
      return { kind: "text", value };
    case "RichText":
      return { kind: "longText", value };
    case "Date":
      return { kind: "date", value };
    case "List": {
      // The options as they'll be saved, so a box still being typed in or left empty isn't offered.
      const options = normaliseAttributeOptions(field.options);
      if (field.allowUserValues) return { kind: "openChoice", value, options };
      if (options.length === 0) return { kind: "needsOptions" };
      return { kind: "pick", value: attributeOptionMatch(options, value) ?? "", options };
    }
    default:
      return null;
  }
}

// ---- only on some items -----------------------------------------------------------------------------------------------------

/** Why "only on some items" can't be turned on for a field that's on every item: the dial has no items yet. Null when it can. */
export function onlySomeItemsRefusal(model: GeneratedDefinition, field: GeneratedNodeAttribute): string | null {
  if (field.appliesToKeys?.length) return null;
  const hasItems = model.sections.some((section) => section.segments.some((segment) => segment.items.length > 0));
  return hasItems ? null : copy.details.onlySomeItemsNoItems;
}

/** The item picker's words. */
export const ITEM_SCOPE_LABELS: ItemScopeTreeLabels = {
  heading: copy.details.whichItems,
  selectAll: copy.details.selectAll,
  selectAllIn: (name) => fill(copy.details.selectAllIn, { name }),
  count: (n, count) => fill(copy.details.itemsTicked, { n, count }),
  lastItem: copy.details.lastItem,
  untitled: copy.build.untitled,
};
